/**
 * Where a visitor came from, remembered until they sign up.
 *
 * An ad click lands with `utm_*` parameters and a click id in the query
 * string. The signup form is usually several pages later — the county
 * landing page, the coverage map, the form — and by then the query string is
 * long gone. So the first page load records what it arrived with — once the
 * server has said a campaign is configured at all, see main.tsx — the record
 * survives in localStorage, and signup sends it to the server, where it is
 * written onto the account once (backend/app/services/attribution.py).
 *
 * First campaign touch wins. A visitor who arrived directly on Monday and
 * came back through an ad on Tuesday is attributed to the ad: the ad is what
 * turned a visit into a signup, and a direct visit has nothing to attribute
 * to. Once a campaign is recorded, a later ad click does not replace it.
 *
 * A visit with nothing to attribute to records nothing at all. Someone who
 * typed the address or followed an untagged link leaves no record in this
 * browser and nothing on the account they go on to create — which is what the
 * Privacy Policy says happens, and the only version of it that is true is the
 * one where the record is never built. `landing_path` and `landed_at` describe
 * our own pages rather than an origin, so they ride along with a record that
 * names one and are never a record by themselves.
 *
 * Everything that can be tested is pure; the two functions that touch the
 * browser are at the bottom and are as thin as they can be.
 */

export const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
  "referrer",
  "landing_path",
  "landed_at",
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string>>;

/** The keys that mean "an ad or a tagged link brought them". */
const CAMPAIGN_KEYS: AttributionKey[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "msclkid",
];

/** Matches the server's bound; anything longer is cut there anyway. */
const MAX_LENGTH = 512;

export const STORAGE_KEY = "ds.attribution";

export interface Landing {
  /** `window.location.search`, "?" included or not. */
  search: string;
  /** `window.location.pathname`. */
  path: string;
  /** `document.referrer` — "" when there was none. */
  referrer: string;
  /** `window.location.host`, so a referrer from our own pages is dropped. */
  ownHost: string;
  /** When they landed, for the record. */
  now: Date;
}

/** Whether the record names a campaign, as opposed to a bare direct visit. */
export function hasCampaign(record: Attribution | null | undefined): boolean {
  if (!record) return false;
  return CAMPAIGN_KEYS.some((key) => Boolean(record[key]));
}

/**
 * Whether the record says where the visitor came from, which is the only
 * reason to keep one: a campaign key, or another site that sent them. A page
 * and a timestamp say where they *arrived*, which we would know anyway.
 */
export function hasOrigin(record: Attribution | null | undefined): boolean {
  if (!record) return false;
  return hasCampaign(record) || Boolean(record.referrer);
}

/**
 * What this page load says about where the visitor came from, or `null` when
 * it says nothing — a typed address, a bookmark, an untagged link, or a click
 * from one of our own pages. Nothing is recorded for those.
 */
export function fromLanding(landing: Landing): Attribution | null {
  const params = new URLSearchParams(landing.search);
  const record: Attribution = {};
  for (const key of CAMPAIGN_KEYS) {
    const value = (params.get(key) ?? "").trim();
    if (value) record[key] = value.slice(0, MAX_LENGTH);
  }
  const referrer = externalReferrer(landing.referrer, landing.ownHost);
  if (referrer) record.referrer = referrer.slice(0, MAX_LENGTH);
  // Before the page and the time are added, not after: they are context for a
  // record, and a record made of nothing but context is the one we promised
  // not to keep.
  if (!hasOrigin(record)) return null;
  record.landing_path = (landing.path || "/").slice(0, MAX_LENGTH);
  record.landed_at = landing.now.toISOString();
  return record;
}

/**
 * A referrer worth keeping: another site's. Our own pages refer to each other
 * on every click, and recording that would overwrite "came from Google" with
 * "came from the coverage map" on the second page.
 */
function externalReferrer(referrer: string, ownHost: string): string {
  if (!referrer) return "";
  try {
    const host = new URL(referrer).host;
    if (!host || host === ownHost) return "";
    return referrer;
  } catch {
    return "";
  }
}

/**
 * The record to keep, given what is stored and what this page load says.
 *
 * - This page load has nothing to say: keep whatever is stored, which is
 *   usually nothing. An untagged or same-site visit neither creates a record
 *   nor overwrites one.
 * - Nothing stored: keep the fresh record — `fromLanding` only produces one
 *   when it names a campaign or another site.
 * - Stored record names a campaign: keep it. First campaign touch wins.
 * - Stored record names only a referrer and the fresh one names a campaign:
 *   the campaign replaces it.
 * - Neither names a campaign: keep the stored one; it is earlier.
 */
export function merge(
  stored: Attribution | null,
  fresh: Attribution | null,
): Attribution | null {
  if (!fresh) return stored;
  if (!stored) return fresh;
  if (hasCampaign(stored)) return stored;
  if (hasCampaign(fresh)) return fresh;
  return stored;
}

/**
 * Only known keys with string values, and only when they add up to a record
 * worth keeping — what a stored blob is allowed to be. The second half is what
 * clears a landing-only blob written by an older build of this file out of a
 * returning visitor's browser instead of sending it at signup.
 */
export function sanitise(raw: unknown): Attribution | null {
  if (!raw || typeof raw !== "object") return null;
  const record: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === "string" && value) {
      record[key] = value.slice(0, MAX_LENGTH);
    }
  }
  return hasOrigin(record) ? record : null;
}

// ---------------------------------------------------------------------------
// The browser
// ---------------------------------------------------------------------------

function readStored(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? sanitise(JSON.parse(raw)) : null;
  } catch {
    // Private mode, a blocked storage API, or a blob somebody edited: all
    // read as "nothing remembered", which is safe.
    return null;
  }
}

/**
 * Record this page load. Called once, at startup, before anything renders;
 * returns what is now remembered.
 */
export function rememberAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  const fresh = fromLanding({
    search: window.location.search,
    path: window.location.pathname,
    referrer: typeof document === "undefined" ? "" : document.referrer,
    ownHost: window.location.host,
    now: new Date(),
  });
  const kept = merge(readStored(), fresh);
  // Nothing to remember and nothing remembered, so nothing is written — not
  // even an empty blob. A visitor who arrived untagged leaves this browser as
  // they found it.
  if (!kept) return null;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  } catch {
    // Nowhere to keep it. Signup will send whatever this page load knows.
  }
  return kept;
}

/** What signup sends. `undefined` rather than `{}` when there is nothing. */
export function storedAttribution(): Attribution | undefined {
  if (typeof window === "undefined") return undefined;
  return readStored() ?? undefined;
}

/** After signup: the record is on the account now and has no other use. */
export function forgetAttribution(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to remove, or nowhere it could have been.
  }
}
